import type { CalendarEvent, Enduser } from "@tellescope/types-models";

// How long before an event's start time we consider it "starting soon" (i.e. worth telling
// the enduser we're waiting on the host, rather than implying the call is imminent from afar)
export const STARTING_SOON_WINDOW_MS = 15 * 60 * 1000;

export interface EventStatus {
  isCancelled: boolean;
  isRescheduled: boolean;
  isUpcoming: boolean;
  isPast: boolean;
  isMeetingLive: boolean;
  isVideoStartingSoon: boolean;
  canJoinVideo: boolean;
}

export const isEventCancelledForEnduser = (event: CalendarEvent, currentEnduser?: Enduser | null): boolean =>
  !!event.cancelledAt || !!event.cancelledGroupAttendees?.find((e) => e.id === currentEnduser?.id);

/**
 * Hook to determine the status of a calendar event.
 * Ensures consistent event status logic across all components.
 *
 * @param event - The calendar event to check status for
 * @param currentEnduser - Optional current enduser (for group event cancellations)
 * @returns Event status details
 *
 * @example
 * const { isCancelled, isUpcoming, canJoinVideo } = useEventStatus(event, currentEnduser);
 * if (canJoinVideo) {
 *   // Show video join button
 * }
 */
export const useEventStatus = (event: CalendarEvent, currentEnduser?: Enduser | null): EventStatus => {
  const isCancelled = isEventCancelledForEnduser(event, currentEnduser);

  const isRescheduled = !!event.rescheduledAt;

  const isUpcoming = event.startTimeInMS > Date.now();
  const isPast = event.startTimeInMS <= Date.now();

  const isMeetingLive = event.meetingStatus === "live";

  const eventEndTimeInMS = event.startTimeInMS + event.durationInMinutes * 60 * 1000;
  const isVideoStartingSoon =
    !isCancelled && Date.now() >= event.startTimeInMS - STARTING_SOON_WINDOW_MS && Date.now() < eventEndTimeInMS;

  // Joining opens the waiting room, which only connects once the provider has started the meeting.
  // A live meeting stays joinable past the scheduled end, since providers often run over.
  const canJoinVideo =
    !isCancelled && event.meetingStatus !== "ended" && (isMeetingLive || Date.now() < eventEndTimeInMS);

  return {
    isCancelled,
    isRescheduled,
    isUpcoming,
    isPast,
    isMeetingLive,
    isVideoStartingSoon,
    canJoinVideo,
  };
};
